import React, { ChangeEvent, Fragment, Ref, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { always, cond, equals } from 'ramda';
import { useUnmount, useWindowSize } from 'react-use';
import { FormattedMessage, useIntl } from 'react-intl';
import Konva from 'konva';

import { renderWhenTrue } from '@utils/rendering';
import { ArtworkSpaceView } from '@components/PDP/artworkSpaceView';
import { SpaceViewStep } from '@hooks/useArtworkSpaceView';
import { Artwork } from '@definitions/artworks.types';
import {
  CheckedIcon,
  DownloadButtonWrapper,
  FieldContainer,
  FieldLabel,
  InputFieldComponent,
  StepTitle,
  UploadInputField,
} from '@components/PDP/pdp.styles';
import { DistanceProps } from '@definitions/common.types';
import { Button, BUTTON_SIZE } from '@components/form';
import { useScroll } from '@hooks/useScroll';
import { commonMessages } from '@components/common';

const ANIMATION_DURATION = 400;

import {
  CloseButton,
  Container,
  Header,
  NextArrow,
  NextStep,
  PrevArrow,
  PrevStep,
  StepNavigation,
  Title,
  InnerContainer,
} from './mobileArtworkSpaceView.styles';

export interface MobileArtworkSpaceViewProps {
  showSpaceView: boolean;
  closeSpaceView: () => void;
  detailViewHeight: number;
  toggleSpaceViewMode: () => void;
  setDistance: (data: DistanceProps) => void;
  artwork: Artwork;
  backgroundImage: string;
  measure: number;
  distance: DistanceProps;
  spaceViewStep: SpaceViewStep;
  setBackgroundImage: (value: string) => void;
  setMeasure: (value: number) => void;
  setSpaceViewStep: (value: number) => void;
  downloadImage: () => void;
  innerRef?: Ref<Konva.Stage>;
}

export const MobileArtworkSpaceView = ({
  showSpaceView,
  closeSpaceView,
  detailViewHeight,
  artwork,
  toggleSpaceViewMode,
  backgroundImage,
  setBackgroundImage,
  measure,
  setMeasure,
  spaceViewStep,
  setSpaceViewStep,
  distance,
  setDistance,
  downloadImage,
  innerRef,
}: MobileArtworkSpaceViewProps) => {
  const { height } = useWindowSize();
  const intl = useIntl();
  const innerContainerRef = useRef<HTMLDivElement | null>(null);
  const containerHeight = height - detailViewHeight - 10;
  const { enableScroll, disableScroll } = useScroll();
  const isNextDisabled =
    (spaceViewStep === SpaceViewStep.First && !backgroundImage) ||
    (spaceViewStep === SpaceViewStep.Second && (!measure || !distance.value));

  useEffect(() => {
    if (showSpaceView) {
      setTimeout(disableScroll, ANIMATION_DURATION);
      return;
    }

    setTimeout(enableScroll, ANIMATION_DURATION);
  }, [showSpaceView]);

  useUnmount(enableScroll);

  const handleChangeValue = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value || '0';
    setMeasure(parseInt(value, 10));
  };

  const renderCheckedIcon = renderWhenTrue(always(<CheckedIcon />));

  const renderFirstStep = () => {
    return (
      <FieldContainer>
        <StepTitle onClick={() => setSpaceViewStep(SpaceViewStep.First)}>
          <FormattedMessage id="mobileArtworkSpaceView.upload" defaultMessage="① Upload:" />
          {renderCheckedIcon(!!backgroundImage)}
        </StepTitle>
        <FieldLabel>
          <FormattedMessage
            id="mobileArtworkSpaceView.uploadDescription"
            defaultMessage="Upload a photo of your room, taken with the camera directly facing the wall"
          />
        </FieldLabel>
        <UploadInputField name="background" accept="image/*" handleChange={setBackgroundImage}>
          <FormattedMessage id="mobileArtworkSpaceView.selectImage" defaultMessage="SELECT IMAGE" />
        </UploadInputField>
      </FieldContainer>
    );
  };
  const renderSecondStep = () => (
    <FieldContainer>
      <StepTitle>
        <FormattedMessage id="mobileArtworkSpaceView.measure" defaultMessage="② Measure:" />
        {renderCheckedIcon(!!distance && !!measure)}
      </StepTitle>
      <FieldLabel>
        <FormattedMessage
          id="mobileArtworkSpaceView.measureDescription"
          defaultMessage="Click any two points in the photo, and let us know the distance, in real life, between them."
        />
      </FieldLabel>
      <InputFieldComponent
        name="measure"
        label="Length:"
        additionalInformation="(inches)"
        min="0"
        pattern="[0-9]*"
        type="number"
        onChange={handleChangeValue}
        value={`${measure}`}
        disabled={spaceViewStep !== SpaceViewStep.Second}
      />
    </FieldContainer>
  );

  const renderThirdStep = () => (
    <FieldContainer>
      <StepTitle>
        <FormattedMessage id="mobileArtworkSpaceView.position" defaultMessage="③ Position:" />
      </StepTitle>
      <Fragment>
        <FieldLabel>
          <FormattedMessage
            id="mobileArtworkSpaceView.positionDescription"
            defaultMessage="Place the artwork thumbnail where you would like to see it in your room. Click to set the thumbnail in place."
          />
        </FieldLabel>
        <DownloadButtonWrapper>
          <Button onClick={downloadImage} fullWidth size={BUTTON_SIZE.XXS}>
            <FormattedMessage id="mobileArtworkSpaceView.download" defaultMessage="Download Image" />
          </Button>
        </DownloadButtonWrapper>
      </Fragment>
    </FieldContainer>
  );
  const renderSteps = cond([
    [equals(SpaceViewStep.First), renderFirstStep],
    [equals(SpaceViewStep.Second), renderSecondStep],
    [equals(SpaceViewStep.Third), renderThirdStep],
  ]) as (step: SpaceViewStep) => any;

  const renderSpaceView = renderWhenTrue(
    always(
      <Container
        initial={{ height: 0 }}
        animate={{ height: containerHeight }}
        exit={{ height: 0 }}
        transition={{ ease: 'linear', duration: ANIMATION_DURATION / 1000 }}
      >
        <InnerContainer containerHeight={containerHeight} ref={innerContainerRef}>
          <Header>
            <Title>
              <FormattedMessage id="mobileArtworkSpaceView.viewSpace" defaultMessage="Preview in Your Space:" />
            </Title>
            <CloseButton onClick={closeSpaceView} aria-label={intl.formatMessage(commonMessages.closeLabel)} />
          </Header>
          <ArtworkSpaceView
            toggleSpaceViewMode={toggleSpaceViewMode}
            setDistance={setDistance}
            artwork={artwork}
            backgroundImage={backgroundImage}
            measure={measure}
            distance={distance}
            spaceViewStep={spaceViewStep}
            ref={innerRef}
          />
          <div>
            {renderSteps(spaceViewStep)}
            <StepNavigation>
              <PrevStep
                onClick={() => setSpaceViewStep(spaceViewStep - 1)}
                hide={spaceViewStep === SpaceViewStep.First}
              >
                <PrevArrow>←</PrevArrow>
                <FormattedMessage id="mobileArtworkSpaceView.back" defaultMessage="Back" />
              </PrevStep>
              <NextStep
                onClick={() => setSpaceViewStep(spaceViewStep + 1)}
                disabled={isNextDisabled}
                hide={spaceViewStep === SpaceViewStep.Third}
              >
                <FormattedMessage id="mobileArtworkSpaceView.next" defaultMessage="Next" />
                <NextArrow>→</NextArrow>
              </NextStep>
            </StepNavigation>
          </div>
        </InnerContainer>
      </Container>
    )
  );
  return <AnimatePresence>{renderSpaceView(showSpaceView)}</AnimatePresence>;
};
